import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Button } from './button';
import { Card, CardContent } from './card';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTour } from './TourContext';

const TourBubble = () => {
  const {
    isActive,
    currentStep,
    totalSteps,
    nextStep,
    prevStep,
    stopTour,
    currentTourStep
  } = useTour();

  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    arrowPosition: 'top'
  });
  const bubbleRef = useRef(null);
 
  const observerRef = useRef(null);
  const timeoutRef = useRef(null);
 
  const waitForElement = (selector, callback, maxAttempts = 40) => {
    let attempts = 0;
    const checkElement = () => {
      const element = document.querySelector(selector);
      if (element && element.offsetParent !== null) {
        callback(element);
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(checkElement, 100);
      } else {
        console.warn(
          `Element ${selector} not found after ${maxAttempts} attempts`
        );
      }
    };
    checkElement();
  };

  const updatePosition = useCallback((targetElement) => {
    if (!targetElement || !targetElement.offsetParent) return;
 
    const rect = targetElement.getBoundingClientRect();
    const bubbleWidth = 320;
    const bubbleHeight = 160;
    const offset = 20;
 
    let top = rect.bottom + offset;
    let left = rect.left + rect.width / 2 - bubbleWidth / 2;
    let arrowPosition = 'top';
 
    if (left < 10) {
      left = 10;
    } else if (left + bubbleWidth > window.innerWidth - 10) {
      left = window.innerWidth - bubbleWidth - 10;
    }
 
    if (top + bubbleHeight > window.innerHeight - 10) {
      top = rect.top - bubbleHeight - offset;
      arrowPosition = 'bottom';
    }
 
    setPosition({ top, left, arrowPosition });
  }, []);
 
  const handlePositionUpdate = useCallback(() => {
    if (!isActive || !currentTourStep) return;
    
    const element = document.querySelector(currentTourStep.target);
    if (element) {
      updatePosition(element);
    }
  }, [isActive, currentTourStep, updatePosition]);
 
  useEffect(() => {
    if (!isActive || !currentTourStep) return;
    
    const selector = currentTourStep.target;
 
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
 
      waitForElement(selector, (targetElement) => {
      updatePosition(targetElement);

      // Cleanup existing observer before creating new one
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      observerRef.current = new MutationObserver((mutations) => {
        let shouldUpdate = false;
        
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && 
              (mutation.attributeName === 'class' || 
               mutation.attributeName === 'style')) {
            shouldUpdate = true;
          }
          
          if (mutation.type === 'childList') {
            shouldUpdate = true;
          }
        });
 
        if (shouldUpdate) {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          timeoutRef.current = setTimeout(() => {
            handlePositionUpdate();
          }, 100);
        }
      });

      observerRef.current.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    });

    const handleUpdate = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(handlePositionUpdate, 50);
    };

    const handleOutsideClick = (event) => {
      if (bubbleRef.current && !bubbleRef.current.contains(event.target)) {
        stopTour();
      }
    };
 
    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate);
 
    document.addEventListener('click', handleUpdate);
    document.addEventListener('mousedown', handleOutsideClick);
 
    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate);
      document.removeEventListener('click', handleUpdate);
      document.removeEventListener('mousedown', handleOutsideClick);
      
      // Cleanup MutationObserver
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      
      // Cleanup timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isActive, currentTourStep, handlePositionUpdate]);
 
  if (!isActive || !currentTourStep) return null;

  const targetRect = document.querySelector(currentTourStep.target)?.getBoundingClientRect();

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" />

      {/* Spotlight effect */}
      <div className="fixed inset-0 pointer-events-none" style={{zIndex:"9999"}}>
        {targetRect && (
          <div
            className="absolute bg-gray-500 bg-opacity-10 rounded-lg border-2 border-blue-400 shadow-lg animate-pulse"
            style={{
              top: `${targetRect.top}px`,
              left: `${targetRect.left}px`,
              width: `${targetRect.width}px`,
              height: `${targetRect.height}px`,
            }}
          />
        )}
      </div>

      {/* Tour Bubble */}
      <div
       ref={bubbleRef}
        className="fixed animate-fade-in transition-all duration-300 ease-in-out"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          width: '320px',
          zIndex: '9999'
        }}
      >
        <Card className="bg-white dark:bg-gray-800 shadow-xl border-2 border-blue-200">
          <CardContent className="p-24">
            <div className="flex justify-between items-start mb-16">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  {currentStep + 1}
                </div>
                <h3 className="font-semibold text-lg">{currentTourStep.title}</h3>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={stopTour}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <p className="text-gray-600 dark:text-gray-300 mb-24 leading-relaxed">
              {currentTourStep.description}
            </p>

            <div className="flex justify-between items-center">
              <div className="flex space-x-1">
                {Array.from({ length: totalSteps }).map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full ${
                      index === currentStep ? 'bg-blue-500' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>

              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={prevStep}
                  disabled={currentStep === 0}
                  className="flex items-center"
                >
                  <ChevronLeft className="h-4 w-4 mr-4" />
                  Previous
                </Button>

                <Button
                  size="sm"
                  onClick={nextStep}
                  className="flex items-center bg-blue-500 hover:bg-blue-600 color-white"
                >
                  {currentStep === totalSteps - 1 ? 'Finish' : 'Next'}
                  {currentStep < totalSteps - 1 && <ChevronRight className="h-4 w-4 ml-4" />}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Arrow */}
        <div
          className={`absolute w-0 h-0 ${
            position.arrowPosition === 'top'
              ? 'border-l-8 border-r-8 border-b-8 border-transparent border-b-blue-200 -top-2 left-1/2 transform -translate-x-1/2'
              : position.arrowPosition === 'bottom'
              ? 'border-l-8 border-r-8 border-t-8 border-transparent border-t-blue-200 -bottom-2 left-1/2 transform -translate-x-1/2'
              : ''
          }`}
        />
      </div>
    </>
  );
};

export default TourBubble;
